import React, { useState, useCallback, useEffect } from 'react'

import { Button, Icon, FileEmpty } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, string } from 'prop-types'

import Pagination from 'Pods/Shared/Components/Pagination/Pagination'
import I18n from 'Pods/Shared/I18n'

import RegularEarningsDetailTable from './RegularEarningsDetailTable'
import RegularEarningsEmptyState from './RegularEarningsEmptyState'
import useEarningsDetail, { DEFAULT_PER_PAGE } from '../../../Hooks/useEarningsDetail'
import {
  EARNINGS_DETAIL_TYPES,
  PER_PAGE_OPTIONS,
  REGULAR_EARNINGS_PANEL_CONFIG,
} from '../../../Static/earningsDetailConfig'
import { formatMonthLabel } from '../../../Static/monthOptions'
import { regularDownloadsColumns } from '../../../Static/regularEarningsColumns'

const { downloads: downloadsEmptyCopy, downloadsForMonth: downloadsForMonthEmptyCopy } =
  REGULAR_EARNINGS_PANEL_CONFIG.emptyState

function RegularEarningsDownloadsTab({ isActive = false, selectedMonth = null }) {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  useEffect(() => { setPage(1) }, [selectedMonth])

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', ...options }),
    []
  )

  const {
    rows, meta, isLoading, generateReport, isReportRequired,
  } = useEarningsDetail({
    type: EARNINGS_DETAIL_TYPES.downloads, page, perPage, month: selectedMonth, enabled: isActive,
  })

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handlePerPageChange = useCallback((nextPerPage) => {
    setPerPage(nextPerPage)
    setPage(1)
  }, [])

  const hasRows = rows.length > 0
  const showEmptyState = !isLoading && !hasRows
  const showFooter = hasRows && meta.totalCount > 0

  return (
    <div className="regular-earnings-detail-panel__tab">
      {showEmptyState ? (
        <RegularEarningsEmptyState
          title={selectedMonth ? downloadsForMonthEmptyCopy.title : downloadsEmptyCopy.title}
          description={selectedMonth
            ? downloadsForMonthEmptyCopy.description(formatMonthLabel(selectedMonth))
            : downloadsEmptyCopy.description}
        />
      ) : (
        <RegularEarningsDetailTable
          rows={rows}
          columns={regularDownloadsColumns}
          isLoading={isLoading}
        />
      )}

      {showFooter && (
        <>
          <div className="regular-earnings-detail-panel__report">
            {isReportRequired ? (
              <p className="cgt-body-base">{translate('profile.my_sales.earnings_detail.report_sent_confirmation')}</p>
            ) : (
              <Button
                type="secondary"
                size="small"
                icon={<Icon svgComponent={FileEmpty} />}
                onClick={generateReport}
              >
                {translate('profile.my_sales.earnings_detail.generate_report')}
              </Button>
            )}
          </div>
          <div className="regular-earnings-detail-panel__pagination">
            <Pagination
              currentPage={meta.page}
              totalPages={Math.max(meta.totalPages, 1)}
              totalCount={meta.totalCount}
              perPage={meta.perPage}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              perPageOptions={PER_PAGE_OPTIONS}
              alwaysShow
            />
          </div>
        </>
      )}
    </div>
  )
}

RegularEarningsDownloadsTab.propTypes = {
  isActive: bool,
  selectedMonth: string,
}

export default RegularEarningsDownloadsTab
